import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { recommendationsApi } from '../api/modules';
import { Drawer, DrawerCloseButton } from '../components/ui/Drawer';
import { useToast } from '../components/toast/Toast';

interface Props {
  open: boolean;
  code: string | null;
  onClose: () => void;
}

export function RecommendationDrawer({ open, code, onClose }: Props) {
  const { show } = useToast();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    enabled: !!code,
    queryKey: ['recommendation', code],
    queryFn: () => recommendationsApi.detail(code!),
  });

  const decide = useMutation({
    mutationFn: ({ decision, outcome }: { decision: string; outcome?: string }) =>
      recommendationsApi.decide(code!, decision, outcome),
    onSuccess: (_d, vars) => {
      show(`${vars.decision === 'accept' ? 'Accepted' : vars.decision === 'reject' ? 'Rejected' : 'Dismissed'} · audit log updated`);
      void qc.invalidateQueries({ queryKey: ['recommendation', code] });
      void qc.invalidateQueries({ queryKey: ['recommendations'] });
      void qc.invalidateQueries({ queryKey: ['dashboard', 'recommendations-summary'] });
    },
    onError: () => show('Decision failed — try again'),
  });

  const isOpen = data?.status === 'open';
  const headerBg =
    data?.status === 'accepted' ? 'bg-green-600 text-white' :
    data?.status === 'rejected' ? 'bg-red-500 text-white' :
    'bg-saffron-500 text-white';

  return (
    <Drawer open={open && !!code} onClose={onClose}>
      <div className={`${data ? headerBg : 'bg-cream-200 text-navy-800'} px-6 py-5 flex items-start justify-between`}>
        <div>
          <div className="text-[10px] uppercase tracking-widest opacity-80">
            {data ? `${data.category} · ${data.id}` : code ?? '—'}
          </div>
          <h2 className="serif-display text-lg font-medium mt-1">AI Recommendation</h2>
          {data && <div className="text-sm opacity-90 mt-2">For: <span className="font-medium">{data.target}</span></div>}
        </div>
        <DrawerCloseButton onClick={onClose} />
      </div>

      <div className="px-6 py-5 flex-1 overflow-y-auto space-y-5">
        {isLoading || !data ? (
          <div className="h-40 bg-cream-100 rounded-sm animate-pulse" />
        ) : (
          <>
            <div>
              <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-2 font-semibold">Recommendation</h3>
              <p className="text-sm text-navy-800 leading-relaxed">{data.text}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Confidence</div>
                <div className={`font-numeric serif-display text-xl font-medium mt-1 ${
                  data.confidence >= 90 ? 'text-green-700' :
                  data.confidence >= 80 ? 'text-saffron-600' : 'text-navy-800'
                }`}>
                  {data.confidence}%
                </div>
                <div className="mt-2 h-1.5 bg-cream-200 rounded-full overflow-hidden">
                  <div className={`h-full ${
                    data.confidence >= 90 ? 'bg-green-600' :
                    data.confidence >= 80 ? 'bg-saffron-500' : 'bg-navy-500'
                  }`} style={{ width: `${data.confidence}%` }} />
                </div>
              </div>
              <div className="bg-cream-50 p-3 rounded-sm">
                <div className="text-[10px] uppercase tracking-widest text-navy-800/50">Source model</div>
                <div className="text-sm font-medium text-navy-800 mt-1">{data.source}</div>
                <div className="text-[11px] text-navy-800/60 mt-1">{data.model_version ?? 'v—'}</div>
              </div>
            </div>

            <div>
              <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-2 font-semibold">Reasoning trace</h3>
              <div className="space-y-2 text-xs">
                {data.reasoning_trace.map((s) => (
                  <div key={s.step} className="p-3 bg-cream-50 rounded-sm">
                    <div className="font-medium text-navy-800">{s.step}. {s.title}</div>
                    <div className="text-navy-800/70 mt-1 leading-relaxed">{s.detail}</div>
                  </div>
                ))}
              </div>
            </div>

            {data.outcome && (
              <div>
                <h3 className="text-xs uppercase tracking-widest text-navy-800/50 mb-2 font-semibold">Outcome</h3>
                <div className={`${data.status === 'accepted' ? 'bg-green-50 border-green-200 text-green-900' : 'bg-red-50 border-red-200 text-red-900'} border rounded-sm p-3 text-sm`}>
                  {data.outcome}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="border-t border-cream-200 px-6 py-4 flex items-center gap-2 bg-cream-50">
        {isOpen && data ? (
          <>
            <button
              type="button"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ decision: 'accept', outcome: `Approved by Director · ${new Date().toLocaleDateString('en-IN')}` })}
              className="flex-1 bg-saffron-500 hover:bg-saffron-600 disabled:opacity-50 text-white text-sm py-2.5 rounded-sm transition"
            >
              {decide.isPending ? 'Working…' : data.primary_action}
            </button>
            <button
              type="button"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ decision: 'dismiss' })}
              className="px-4 py-2.5 border border-cream-300 text-navy-800/80 hover:bg-cream-100 text-sm rounded-sm"
            >
              Dismiss
            </button>
            <button
              type="button"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ decision: 'reject', outcome: 'Rejected · reasons logged' })}
              className="px-4 py-2.5 border border-red-300 text-red-600 hover:bg-red-50 text-sm rounded-sm"
            >
              Reject
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-navy-800 hover:bg-navy-700 text-white text-sm py-2.5 rounded-sm"
          >
            Close
          </button>
        )}
      </div>
    </Drawer>
  );
}
